export const USER_CAPABILITIES_EXPIRY_REASON: number = 24001; 
export const USER_CAPABILITIES_SYNC: number = 24002; 
export const USER_CAPABILITIES_OPEN_DOOR: number = 24003; 
export const USER_CAPABILITIES_ENGINE_CONTROL: number = 24004; 
export const USER_CAPABILITIES_BIO_USER_COUNT: number = 24005; 

import { ROLES } from './preset-constants';

/**
 * Returns the set of advanced capability IDs that are applicable for the given
 * profile (role) ID when editing an operator login's Advanced Permissions.
 *
 * Rules:
 *  - ENGINE_CONTROL  – always shown (available to everyone)
 *  - SYNC            – only if the role has access to the hardware menu
 *  - OPEN_DOOR       – only if the role has access to the Door view under hardware
 *                      OR the commands widget under dashboard
 *  - EXPIRY_REASON   – only if the role has access to the enrolment menu
 *                      (Manage Operational Compliance)
 *  - BIO_USER_COUNT  – only if the role has access to the hardware menu
 *
 * Returns null for unknown / custom roles so all capabilities remain visible.
 */
export const getAllowedCapabilityIdsForProfile = (profileId: number): Set<number> | null => {
    const knownRoles = new Set([
        ROLES.ID_SYSTEM_OWNER,
        ROLES.ID_SITE_ADMIN,
        ROLES.ID_INSTALLER,
        ROLES.ID_REPORTS,
        ROLES.ID_RECEPTION,
        ROLES.ID_GUARD,
    ]);

    if (!knownRoles.has(profileId)) {
        // Custom / unknown role – do not restrict
        return null;
    }

    // Roles that have access to the hardware menu (route: "hardware")
    const rolesWithHardware = new Set([ROLES.ID_SYSTEM_OWNER, ROLES.ID_SITE_ADMIN, ROLES.ID_INSTALLER]);

    // Roles that can open doors: hardware roles (have doors route) or
    // dashboard roles (installer + guard have dashboard → commands widget)
    const rolesWithOpenDoor = new Set([
        ROLES.ID_SYSTEM_OWNER,
        ROLES.ID_SITE_ADMIN,
        ROLES.ID_INSTALLER,
        ROLES.ID_GUARD,
    ]);

    // Roles that have access to the enrolment menu (route: "enrolment")
    const rolesWithEnrolment = new Set([
        ROLES.ID_SYSTEM_OWNER,
        ROLES.ID_SITE_ADMIN,
        ROLES.ID_RECEPTION,
    ]);

    const allowed = new Set<number>([USER_CAPABILITIES_ENGINE_CONTROL]);

    if (rolesWithHardware.has(profileId)) {
        allowed.add(USER_CAPABILITIES_SYNC);
        allowed.add(USER_CAPABILITIES_BIO_USER_COUNT);
    }

    if (rolesWithOpenDoor.has(profileId)) {
        allowed.add(USER_CAPABILITIES_OPEN_DOOR);
    }

    if (rolesWithEnrolment.has(profileId)) {
        allowed.add(USER_CAPABILITIES_EXPIRY_REASON);
    }

    return allowed;
};